<script setup lang="ts">
// One enum column's filter as an explicit checkbox panel. The native
// multi-select it replaces was collapsed to a single line (size="1"), so its
// options were invisible until opened and a second value needed a ctrl-click —
// unusable for the Feature `type` vocabulary (~49 entries) and no better for
// the short ones. The trigger summarizes the current selection, so the header
// still says what is filtered without the panel open; the panel lists every
// option with a real checkbox and scrolls when the vocabulary is long.
//
// This component is presentational: the parent owns the clause. A toggle emits
// the whole new value list (empty = cleared), which the parent turns into the
// one enum filter clause through the same path the multi-select used, so the
// wire shape is unchanged. It carries no local selection state.
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

import {
	type MapReviewColumn,
	type MapReviewColumnOption,
	enumSelectionLabels,
	enumSelectionSummary,
	selectAllEnumValues,
	toggleEnumSelection,
} from "./columns";

interface Props {
	column: MapReviewColumn;
	/** The column's option vocabulary, resolved by the caller (store-backed
	 * product/intervention lists included). */
	options: MapReviewColumnOption[];
	/** The column's current enum values: what the control renders checked. */
	values: (string | number)[];
}
const props = defineProps<Props>();
const emit = defineEmits<{ change: [values: (string | number)[]] }>();

// The panel is teleported to <body> and positioned against the trigger's rect:
// inside the table's own scroll container it would be clipped by the column's
// overflow far before 49 options showed.
const PANEL_MIN_WIDTH = 220;
const PANEL_MAX_HEIGHT = 320;

const open = ref(false);
const rootEl = ref<HTMLElement | null>(null);
const triggerEl = ref<HTMLButtonElement | null>(null);
const panelEl = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});

const labels = computed(() => enumSelectionLabels(props.options, props.values));
const summary = computed(() =>
	enumSelectionSummary(props.options, props.values),
);
const hasSelection = computed(() => props.values.length > 0);
const triggerLabel = computed(() =>
	hasSelection.value
		? `Filter ${props.column.label}: ${summary.value}`
		: `Filter ${props.column.label}`,
);
const title = computed(() =>
	hasSelection.value
		? `${props.column.label}: ${labels.value.join(", ")}`
		: `Filter ${props.column.label}`,
);

function isChecked(option: MapReviewColumnOption): boolean {
	return props.values.some((v) => String(v) === String(option.value));
}

function toggle(option: MapReviewColumnOption): void {
	emit(
		"change",
		toggleEnumSelection(props.options, props.values, option.value),
	);
}

function clear(): void {
	emit("change", []);
}

function selectAll(): void {
	emit("change", selectAllEnumValues(props.options));
}

function positionPanel(): void {
	const trigger = triggerEl.value;
	if (!trigger) return;
	const rect = trigger.getBoundingClientRect();
	const below = window.innerHeight - rect.bottom - 6;
	const above = rect.top - 6;
	// Flip above the trigger when there is no room under it, as the filter row
	// sits wherever the table lands in the page.
	const openUp = below < 160 && above > below;
	const width = Math.max(rect.width, PANEL_MIN_WIDTH);
	const left = Math.min(
		Math.max(4, rect.left),
		Math.max(4, window.innerWidth - width - 4),
	);
	const maxHeight = Math.min(
		PANEL_MAX_HEIGHT,
		Math.max(120, openUp ? above : below),
	);
	panelStyle.value = {
		position: "fixed",
		left: `${left}px`,
		width: `${width}px`,
		maxHeight: `${maxHeight}px`,
		...(openUp
			? { bottom: `${window.innerHeight - rect.top + 2}px` }
			: { top: `${rect.bottom + 2}px` }),
	};
}

async function openPanel(): Promise<void> {
	open.value = true;
	await nextTick();
	positionPanel();
	// Land on the first control in the panel (the "All" bulk button) so the
	// whole panel — bulk actions, then every checkbox, then Done — is reachable
	// by tabbing forward, not just the checkbox list.
	panelEl.value?.querySelector<HTMLButtonElement>("button")?.focus();
}

function closePanel(returnFocus = false): void {
	if (!open.value) return;
	open.value = false;
	if (returnFocus) triggerEl.value?.focus();
}

// Escape and an outside pointer close the panel. Both listen on the document
// so they hold whether focus is on the trigger, on a checkbox, or nowhere.
function onTriggerClick(): void {
	if (open.value) closePanel(true);
	else void openPanel();
}

function onDocumentPointerDown(event: PointerEvent): void {
	const target = event.target as Node | null;
	if (!target) return;
	if (rootEl.value?.contains(target) || panelEl.value?.contains(target)) return;
	closePanel();
}

function onDocumentKeydown(event: KeyboardEvent): void {
	if (event.key === "Escape") closePanel(true);
}

function bindListeners(): void {
	document.addEventListener("pointerdown", onDocumentPointerDown, true);
	document.addEventListener("keydown", onDocumentKeydown, true);
	window.addEventListener("resize", positionPanel);
	window.addEventListener("scroll", positionPanel, true);
}

function unbindListeners(): void {
	document.removeEventListener("pointerdown", onDocumentPointerDown, true);
	document.removeEventListener("keydown", onDocumentKeydown, true);
	window.removeEventListener("resize", positionPanel);
	window.removeEventListener("scroll", positionPanel, true);
}

watch(open, (isOpen) => (isOpen ? bindListeners() : unbindListeners()));

onBeforeUnmount(unbindListeners);
</script>

<template>
	<div ref="rootEl" class="ef-root">
		<button
			ref="triggerEl"
			type="button"
			class="ef-trigger"
			:class="{
				'ef-open': open,
				'ef-empty': !hasSelection,
				'ef-has-clear': hasSelection,
			}"
			:aria-expanded="open"
			:aria-label="triggerLabel"
			:title="title"
			@click="onTriggerClick"
		>
			<span class="ef-summary">{{ summary || "All" }}</span>
			<i class="bi bi-chevron-down ef-caret" aria-hidden="true"></i>
		</button>
		<button
			v-if="hasSelection"
			type="button"
			class="ef-clear"
			:aria-label="`Clear ${column.label} filter`"
			:title="`Clear ${column.label} filter`"
			@click="clear"
		>
			<i class="bi bi-x" aria-hidden="true"></i>
		</button>

		<Teleport to="body">
			<div
				v-if="open"
				ref="panelEl"
				class="ef-panel"
				:style="panelStyle"
				role="group"
				:aria-label="`Filter ${column.label}`"
			>
				<div class="ef-bulk">
					<button
						type="button"
						class="ef-action"
						:disabled="options.length === 0"
						:aria-label="`Select all ${column.label} options`"
						:title="`Select all ${column.label} options`"
						@click="selectAll"
					>
						All
					</button>
					<button
						type="button"
						class="ef-action"
						:disabled="!hasSelection"
						:aria-label="`Clear ${column.label} filter`"
						:title="`Clear ${column.label} filter`"
						@click="clear"
					>
						None
					</button>
				</div>
				<div class="ef-list">
					<label
						v-for="option in options"
						:key="String(option.value)"
						class="ef-option"
					>
						<input
							type="checkbox"
							:checked="isChecked(option)"
							@change="toggle(option)"
						/>
						<span class="ef-option-label">{{ option.label }}</span>
					</label>
					<div v-if="options.length === 0" class="ef-none">
						No options available.
					</div>
				</div>
				<div class="ef-actions">
					<button type="button" class="ef-action" @click="closePanel(true)">
						Done
					</button>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<style scoped>
.ef-root {
	position: relative;
	width: 100%;
}

.ef-trigger {
	display: flex;
	align-items: center;
	gap: 3px;
	width: 100%;
	height: 24px;
	padding: 0 14px 0 4px;
	font-size: 0.6875rem;
	line-height: 22px;
	text-align: left;
	color: #212529;
	background: #fff;
	border: 1px solid #ced4da;
	border-radius: 2px;
	cursor: pointer;
}

.ef-trigger.ef-has-clear {
	padding-right: 28px;
}

.ef-trigger.ef-empty {
	color: #6c757d;
}

.ef-trigger:hover {
	border-color: #adb5bd;
}

.ef-trigger.ef-open {
	border-color: #86b7fe;
	box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.15);
}

.ef-summary {
	flex: 1 1 auto;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.ef-caret {
	flex: 0 0 auto;
	font-size: 0.5625rem;
	color: #6c757d;
}

.ef-clear {
	position: absolute;
	top: 50%;
	right: 2px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	padding: 0;
	font-size: 0.75rem;
	line-height: 1;
	color: #6c757d;
	background: transparent;
	border: 0;
	border-radius: 2px;
	transform: translateY(-50%);
	cursor: pointer;
}

.ef-clear:hover {
	color: #212529;
	background: #e9ecef;
}

.ef-panel {
	z-index: 2000;
	display: flex;
	flex-direction: column;
	font-size: 0.75rem;
	color: #212529;
	background: #fff;
	border: 1px solid #ced4da;
	border-radius: 4px;
	box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
}

.ef-list {
	flex: 1 1 auto;
	min-height: 0;
	padding: 4px 0;
	overflow-y: auto;
}

.ef-option {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 3px 8px;
	white-space: nowrap;
	cursor: pointer;
}

.ef-option:hover {
	background: #f1f3f5;
}

.ef-option input {
	flex: 0 0 auto;
	margin: 0;
}

.ef-option-label {
	overflow: hidden;
	text-overflow: ellipsis;
}

.ef-none {
	padding: 6px 8px;
	color: #6c757d;
}

.ef-bulk {
	display: flex;
	flex: 0 0 auto;
	gap: 6px;
	padding: 4px 8px;
	border-bottom: 1px solid #dee2e6;
}

.ef-actions {
	display: flex;
	flex: 0 0 auto;
	justify-content: flex-end;
	gap: 6px;
	padding: 4px 8px;
	border-top: 1px solid #dee2e6;
}

.ef-action {
	padding: 2px 8px;
	font-size: 0.6875rem;
	color: #212529;
	background: #f8f9fa;
	border: 1px solid #ced4da;
	border-radius: 2px;
	cursor: pointer;
}

.ef-action:disabled {
	color: #adb5bd;
	cursor: default;
}

.ef-action:not(:disabled):hover {
	background: #e9ecef;
}
</style>
